import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import type { WebAssetListItemDto, WebAssetViewDto, AssetVersionViewDto, ApprovalCommentViewDto, SignOffDto, EmailCopyActivationViewDto } from '@/api/asset'
import type { ApiResponse } from '@/api/client'
import type { AssetQaRunViewDto } from '@/types/assetQa'
import { assetApi } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { useAIChatContextUpdater } from '@/hooks'
import { BRAND_CLASSES } from '@/constants/theme'
import { useAuth } from '@/auth/useAuth'
import { useAssetQaVisible } from '@/hooks/useAssetQaVisible'
import { useAssetSignalR } from '@/hooks/useAssetSignalR'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useSasUriRefresh } from '@/hooks/useSasUriRefresh'
import { isWebsterAssetType } from '@/components/webs/constants/assetMeta'
import { AssetReviewTopBar } from './AssetReviewTopBar'
import { AssetDocumentViewer } from './AssetDocumentViewer'
import { AssetReviewSidePanel } from './AssetReviewSidePanel'
import { useAssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { useSendTestEmail } from '@/hooks/useSendTestEmail'
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview'
import { useWebLoader } from '@/hooks/useWebLoader'
import { isOnlineEventType } from '@/components/webs/constants/webType'
import { isOnDemandMode } from '@/utils/onlineEventMode'
import { EmailTemplatePreview } from '@/components/assets/EmailTemplatePreview'
import { EmailCopyPreview } from '@/components/assets/EmailCopyPreview'

import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { qaStateKey } from './assetQaStateKey'

interface AssetReviewOverlayProps {
  assets: WebAssetListItemDto[]
  initialIndex: number
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onClose: () => void
  onAssetUpdated?: () => void
  onAssetChange?: (assetId: string) => void
  onEditInWebster?: (assetId: string, assetType: string) => void
  /**
   * Sends the reader to the web tab that owns a QA gap's field (ORB-889).
   *
   * ⚑ Closing the overlay is the caller's job, not this component's. The reader is being
   * sent to a different tab of the web that the overlay is drawn on top of, so leaving it
   * open would land them on the right screen with the wrong thing covering it.
   */
  onGoToWebTab?: (tab: WebTabKey) => void
}

export function AssetReviewOverlay({
  assets,
  initialIndex,
  clientCompanyId,
  workspaceId,
  campaignId,
  onClose,
  onAssetUpdated,
  onAssetChange,
  onEditInWebster,
  onGoToWebTab,
}: AssetReviewOverlayProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [assetDetail, setAssetDetail] = useState<WebAssetViewDto | null>(null)
  // The web behind the asset, for the thank-you preview's state toggle. It loads once the asset does,
  // and the hook no-ops on the blank id until then.
  const { web } = useWebLoader(workspaceId, assetDetail?.WebId ?? '')
  // An On-demand recording never airs, so its pages have no before and after (ORB-1076).
  const hasEventStates = isOnlineEventType(web?.Type) && !isOnDemandMode(web?.OnlineEvent?.ModeType)
  const [versions, setVersions] = useState<AssetVersionViewDto[]>([])
  const [comments, setComments] = useState<ApprovalCommentViewDto[]>([])
  const [selectedVersionNumber, setSelectedVersionNumber] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [viewerExpanded, setViewerExpanded] = useState(false)
  // LP HTML preview state (fetched via the resolving version-HTML endpoint — ORB-419)
  const [lpHtml, setLpHtml] = useState<string | null>(null)
  const [lpThankYouHtml, setLpThankYouHtml] = useState<string | null>(null)
  // Past-version HTML for the Email Template/Copy preview (null = preview the current draft live)
  const [emailVersionHtml, setEmailVersionHtml] = useState<string | null>(null)
  const [copyActivations, setCopyActivations] = useState<EmailCopyActivationViewDto[]>([])

  // Null once loaded means the version was never checked, which is different from a clean result.
  const [qaRun, setQaRun] = useState<AssetQaRunViewDto | null>(null)
  const [qaLoading, setQaLoading] = useState(false)
  // Held apart from qaRun: a read that failed and a version that was never checked look the same
  // in a null, and telling a reviewer "not checked" when the result exists is the wrong lie.
  const [qaLoadError, setQaLoadError] = useState<string | null>(null)
  // What the three pieces of QA state above describe. Without it there is no way to tell "loaded and
  // clean" from "about to load for a version we have not fetched yet".
  //
  // ⚑⚑ The version AND whether QA applied, as one value (ORB-982). It used to be the version alone,
  // which made a visibility change invisible to the render-time match below: the key still agreed, so
  // state loaded while QA was visible was treated as describing the moment after it was revoked. One
  // value rather than two pieces of state that must move together, because half-updating it is the
  // failure the match exists to prevent. Raised by Copilot on PR 243.
  const [qaStateFor, setQaStateFor] = useState<string | undefined>(undefined)
  const { showSuccess, showError, showErrorUnless422 } = useToast()
  const { updateContext } = useAIChatContextUpdater()
  const { userProfile } = useAuth()
  const qaVisible = useAssetQaVisible()

  const currentAsset = assets[currentIndex]

  // --- SignalR callbacks (stable via refs to avoid dependency cascades) ---

  const currentAssetRef = useRef(currentAsset)
  currentAssetRef.current = currentAsset
  const onAssetUpdatedRef = useRef(onAssetUpdated)
  onAssetUpdatedRef.current = onAssetUpdated

  const handleCommentAdded = useCallback((comment: ApprovalCommentViewDto) => {
    setComments(prev =>
      prev.some(c => c.CommentId === comment.CommentId) ? prev : [...prev, comment]
    )
    setAssetDetail(prev => prev ? { ...prev, ETag: comment.ETag } : prev)
  }, [])

  const handleUploadCompleted = useCallback((notification: { versionNumber: number }) => {
    showSuccess('Upload complete', `v${notification.versionNumber} is ready.`)
    if (currentAssetRef.current) loadAssetDataRef.current(currentAssetRef.current.Id)
    onAssetUpdatedRef.current?.()
  }, [showSuccess])

  const handleUploadFailed = useCallback((notification: { errorMessage?: string | null }) => {
    showError('Upload failed', notification.errorMessage || 'The file could not be processed from cloud storage.')
  }, [showError])

  /**
   * Holds the current QA-completed handler so the subscription does not depend on it (ORB-889).
   *
   * ⚑ Two reasons, and the second is the one that bites. The real handler is declared further down,
   * after `qaApplies` and `selectedVersionId`, and a `const` is not hoisted — passing it directly
   * here is a temporal-dead-zone error at render. And it changes whenever the selected version
   * changes, which would tear down and rejoin the SignalR group on every version switch, during
   * which events are missed silently.
   */
  /** The version on screen right now, so an in-flight refresh can tell whether it is still wanted. */
  const selectedVersionIdRef = useRef<string | undefined>(undefined)
  const qaCompletedRef = useRef<((notification: { AssetVersionId: string }) => void) | null>(null)
  const handleQaCompletedStable = useCallback(
    (notification: { AssetVersionId: string }) => qaCompletedRef.current?.(notification), [])

  const { subscribeToUploadStatus, preJoinForUpload } = useAssetSignalR({
    assetId: currentAsset?.Id,
    userId: userProfile?.Id,
    workspaceId,
    onCommentAdded: handleCommentAdded as (...args: unknown[]) => void,
    onUploadCompleted: handleUploadCompleted,
    onUploadFailed: handleUploadFailed,
    // ⚑ Undefined rather than a handler that returns early (ORB-982). useAssetSignalR skips the
    // subscription entirely when this is absent, so a customer's session does not join a group whose
    // every notification it would discard.
    onQaCompleted: qaVisible ? handleQaCompletedStable : undefined,
  })

  // --- AI chat context ---

  useEffect(() => {
    if (currentAsset) {
      updateContext({
        campaignId,
        assetId: currentAsset.Id,
        location: ChatLocations.WebAssetEditor,
        entityType: ChatEntityTypes.Asset,
        entityId: currentAsset.Id,
        viewMode: ChatViewModes.View,
        availableFields: [],
        availableActions: ['CopyToClipboard'],
      })
    }
  }, [workspaceId, campaignId, currentAsset?.Id, updateContext])

  // --- Data loading ---

  const loadAssetData = useCallback(async (assetId: string) => {
    setLoading(true)
    setError(undefined)
    setAssetDetail(null)
    setVersions([])
    setComments([])

    const [detailRes, versionsRes, commentsRes] = await Promise.all([
      assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId),
      assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId),
      assetApi.getAssetComments(clientCompanyId, workspaceId, campaignId, assetId),
    ])

    if (detailRes.data) {
      setAssetDetail(detailRes.data)
      setSelectedVersionNumber(detailRes.data.CurrentVersionNumber)
    } else if (detailRes.error) {
      setError(detailRes.error)
    }

    if (versionsRes.data) {
      setVersions(versionsRes.data)

      // Re-subscribe for upload result if there's an in-progress URL upload
      const pendingVersion = versionsRes.data.find(
        v => v.UploadStatus === 'Pending' || v.UploadStatus === 'Downloading'
      )
      if (pendingVersion) {
        subscribeToUploadStatus(pendingVersion.Id)
      }
    }

    if (commentsRes.data) {
      setComments(commentsRes.data)
    }

    setLoading(false)
  }, [clientCompanyId, workspaceId, campaignId, subscribeToUploadStatus])

  const loadAssetDataRef = useRef(loadAssetData)
  loadAssetDataRef.current = loadAssetData

  useEffect(() => {
    if (currentAsset) {
      loadAssetDataRef.current(currentAsset.Id)
    }
  }, [currentAsset?.Id])

  // --- Load LP HTML for the selected promoted Landing Page version ---
  // ORB-419: route through the API (not a direct SAS blob fetch) so the event-display tokens and the
  // event-runtime sentinel resolve against the Web's current online-event timing. The stored version
  // blob stays tokenized because deploy substitutes from it; resolving on read mirrors the editor's
  // /Draft preview. The old direct-blob fetch bypassed the resolver and showed raw {{ORBITALX_EVENT_*}}
  // placeholders + an inert countdown for online-event pages.
  useEffect(() => {
    if (!assetDetail || assetDetail.Type !== 'Landing Page') return
    // Clear stale HTML up front — even when we skip the fetch below — so a versionless LP (or the brief
    // selectedVersionNumber===0 window while paging between assets) never shows the previous page.
    setLpHtml(null)
    setLpThankYouHtml(null)
    if (selectedVersionNumber <= 0) return
    let cancelled = false
    assetApi.getLandingPageVersionHtml(clientCompanyId, workspaceId, campaignId, assetDetail.Id, selectedVersionNumber)
      .then(res => {
        if (cancelled) return
        if (res.data) {
          setLpHtml(res.data.Html)
          setLpThankYouHtml(res.data.ThankYouHtml)
        } else if (res.error) {
          // A lapsed token or transient failure leaves the preview blank rather than silently stale;
          // log (matching the email version-preview path) instead of swallowing.
          console.error('[AssetReview] landing page version HTML load failed:', res.error)
        }
      })
      .catch(err => console.error('[AssetReview] landing page version HTML fetch failed', err))
    return () => { cancelled = true }
  }, [assetDetail?.Type, assetDetail?.Id, selectedVersionNumber, clientCompanyId, workspaceId, campaignId])

  // --- Load the QA result for the selected version (ORB-839) ---
  // Only the two types the upload checkpoint actually checks. Rendering an empty "not checked"
  // panel on a slide deck would read as a gap in the feature rather than as a type nobody checks.
  //
  // ⚑⚑ `qaVisible` first (ORB-982). This one flag is the whole dashboard gate: the side panel
  // already renders the Quality check section and the verdict pill only when `qaApplies` is true,
  // and the two effects below only fetch when it is. So a customer neither sees the section nor
  // causes the read, and there is no second place to keep in step.
  const qaApplies = qaVisible
    && (assetDetail?.Type === 'LinkedIn Asset' || assetDetail?.Type === 'LinkedIn Event Asset')
  const selectedVersion = versions.find(v => v.VersionNumber === selectedVersionNumber)
  const selectedVersionId = selectedVersion?.Id
  // ORB-303: a copy is reviewed as the version being signed off, never its live draft. A version kept with its subject
  // lines renders by number; one saved before then shows its stored HTML under the copy's own subject.
  const copyVersionNumber = assetDetail?.Type === 'Email Copy' && selectedVersion?.Subject ? selectedVersion.VersionNumber : undefined

  // ⚑ Matched at RENDER time, not cleared in an effect. The effect below resets this state when the
  // selected version changes, but an effect runs after the render that caused it — so for one frame
  // the panel described the previous version. A QA verdict on an asset under review is the one value
  // that must never be briefly wrong, and a reviewer paging versions is exactly when they read it.
  //
  // ⚑ All THREE, not just the run. Matching only qaRun still flashed the previous version's load
  // error, or "has not been checked" against a version whose fetch had not started, because loading
  // and error were left describing the old one. Stale state has to be treated as a unit: until the
  // stored state belongs to the selected version, the honest answer is "loading".
  // Raised by Copilot on PR #137 and #138.
  // ⚑ Gated on the asset having loaded, not just on the ids agreeing. Both are undefined before
  // versions arrive, so an id comparison alone matched and the panel reported "has not been checked"
  // for a tick before any fetch was possible — the same transient lie this guard exists to stop,
  // moved earlier. Waiting for `loading` separates "nothing has loaded yet", which is loading, from
  // "this asset has no version to check", which is genuinely not-checked and must not spin forever.
  // Raised by Aikido on PR #138.
  // ⚑ Includes whether QA applies, so BOTH visibility transitions invalidate the stored state at
  // render rather than in the effect that follows it. Revoking access otherwise left the previous
  // verdict on screen for one paint, and granting it reported "has not been checked" for one paint
  // before the fetch had started — the same transient lie as a version change, from a different
  // cause. Raised by Copilot on PR 243.
  const qaStateIsForSelectedVersion =
    !loading && qaStateFor === qaStateKey(qaApplies, selectedVersionId)
  const qaRunForSelectedVersion = qaStateIsForSelectedVersion ? qaRun : null
  const qaLoadingForSelectedVersion = qaStateIsForSelectedVersion ? qaLoading : true
  const qaLoadErrorForSelectedVersion = qaStateIsForSelectedVersion ? qaLoadError : null

  useEffect(() => {
    if (!qaApplies || !assetDetail || !selectedVersionId) {
      setQaRun(null)
      setQaLoadError(null)
      setQaLoading(false)
      setQaStateFor(qaStateKey(qaApplies, selectedVersionId))
      return
    }

    // Cleared up front so paging between assets never shows the previous asset's verdict against
    // this one — the worst possible failure for a panel whose whole job is to be trusted.
    setQaRun(null)
    setQaLoadError(null)
    setQaLoading(true)
    setQaStateFor(qaStateKey(qaApplies, selectedVersionId))

    let cancelled = false
    assetApi.getVersionQaRun(clientCompanyId, workspaceId, assetDetail.Id, selectedVersionId)
      .then(res => {
        if (cancelled) return
        // A 204 arrives as undefined data with no error: the version was never checked, which the
        // panel says in words rather than showing an empty result that reads as clean.
        if (res.error) {
          console.error('[AssetReview] QA run load failed:', res.error)
          setQaLoadError(res.error)
        } else {
          setQaRun(res.data ?? null)
        }
      })
      .catch(err => {
        if (cancelled) return
        console.error('[AssetReview] QA run fetch failed', err)
        setQaLoadError(err instanceof Error ? err.message : 'Please try again.')
      })
      .finally(() => { if (!cancelled) setQaLoading(false) })

    return () => { cancelled = true }
  }, [qaApplies, assetDetail?.Id, selectedVersionId, clientCompanyId, workspaceId])

  /**
   * Refetches the run when one lands for the version on screen (ORB-889).
   *
   * ⚑ QA takes about fifteen seconds and the panel used to fetch once, so whoever had just uploaded
   * — the person most likely to be watching — sat on "This version has not been checked" until they
   * reloaded. It is the honest-sounding message, which is what made it bad: nothing about it looked
   * stale.
   *
   * ⚑⚑ Deliberately does NOT clear state first, unlike the effect above. That one clears because it
   * is switching versions and must never show the previous version's verdict against this one; this
   * one is replacing a result with a newer result for the SAME version, and blanking the panel
   * mid-read would make a quiet refresh look like a reload.
   *
   * Ignores runs for other versions. The notification is group-scoped to the asset, not the version,
   * so a run finishing for v3 must not overwrite what is on screen for v4.
   */
  const handleQaCompleted = useCallback((notification: { AssetVersionId: string }) => {
    if (!qaApplies || !assetDetail || !selectedVersionId) return
    if (notification.AssetVersionId !== selectedVersionId) return

    const requestedVersionId = selectedVersionId

    assetApi.getVersionQaRun(clientCompanyId, workspaceId, assetDetail.Id, requestedVersionId)
      .then(res => {
        // ⚑⚑ Checked AGAIN on arrival, against the live selection rather than the one captured when
        // the request went out. The guard above only proves the version was current when the event
        // arrived; a reader who switches version during the in-flight read would otherwise have v1's
        // run written over v2's, and — worse — the state key set back to v1. The panel decides
        // what to draw by comparing that against the selected version, so it would then match
        // nothing and sit on its loading path forever, with no further load coming to rescue it.
        // Raised by Copilot on PR #144.
        if (selectedVersionIdRef.current !== requestedVersionId) return

        if (res.error) {
          // Left as it was rather than surfaced: the panel already holds a correct answer, and
          // replacing it with an error because a background refresh failed is a downgrade.
          console.error('[AssetReview] QA run refresh failed:', res.error)
          return
        }
        if (res.data) {
          setQaRun(res.data)
          setQaLoadError(null)
          setQaStateFor(qaStateKey(qaApplies, requestedVersionId))
        }
      })
      .catch(err => console.error('[AssetReview] QA run refresh failed', err))
  }, [qaApplies, assetDetail, selectedVersionId, clientCompanyId, workspaceId])

  useEffect(() => { qaCompletedRef.current = handleQaCompleted }, [handleQaCompleted])
  useEffect(() => { selectedVersionIdRef.current = selectedVersionId }, [selectedVersionId])

  const handleOverrideQaFinding = useCallback(async (
    checkId: string, code: string, reason: string, groupKey?: string
  ) => {
    if (!assetDetail || !selectedVersionId) return

    // ⚑ qaRun?.Id is the run the panel is showing. handleQaCompleted can replace it while the
    // override modal is open — the modal does not close on a refresh — so sending it is what stops
    // a decision landing on a run the person never saw.
    const res = await assetApi.overrideQaFinding(
      clientCompanyId, workspaceId, assetDetail.Id, selectedVersionId, checkId, code, reason,
      groupKey, qaRun?.Id)

    if (res.error || !res.data) {
      console.error('[AssetReview] QA override failed:', res.error)
      // Thrown for the modal to catch and show. It also toasts, because a reviewer who has scrolled
      // the panel may not have the modal's own message in view.
      const message = res.error ?? 'The override could not be saved.'
      showError('Override failed', message)
      throw new Error(message)
    }

    // Re-render from what the server stored rather than patching the local copy, so the panel can
    // never show an override that did not persist.
    setQaRun(res.data)

    // ⚑⚑ Tell the list too. An override changes a count the Assets tab is drawing — the chip beside
    // this asset — and without this it kept the pre-override number until something else happened to
    // reload: panel said "5 to fix", the row behind it said 6, about the same run.
    //
    // The same refresh the upload path already uses, and it is the whole list rather than this row
    // because `loadAssets` fetches the assets and their QA together (ORB-889), so one call keeps the
    // two in step. A row-level patch would put the chip's arithmetic in a second place, which is the
    // disagreement this is fixing.
    onAssetUpdatedRef.current?.()
    // showError is closed over and belongs here: useToast returns a new identity when its provider
    // re-renders, so omitting it pins this callback to a stale toast context and the failure branch
    // above can post to a dispatcher nobody is listening to. Raised by Copilot on PR #137.
    // ⚑ qaRun?.Id belongs here for the same reason showError does: pinning this callback to a stale
    // run id would send the PREVIOUS run's id and turn every override after a refresh into a 409.
  }, [assetDetail?.Id, selectedVersionId, clientCompanyId, workspaceId, showError, qaRun?.Id])

  // --- Load an email version's stored HTML for preview ---
  // A template's current version previews live, and a past one is rendered through the same substituting preview
  // endpoint by passing its stored HTML as an override (ORB-301). A copy is always reviewed as a saved version
  // (ORB-303): one kept with its subject renders by number, so only one saved before then needs its HTML fetched here.
  useEffect(() => {
    const type = assetDetail?.Type
    if (type !== 'Email Template' && type !== 'Email Copy') return
    const version = versions.find(v => v.VersionNumber === selectedVersionNumber)
    const needsStoredHtml = type === 'Email Template'
      ? !!assetDetail && selectedVersionNumber !== assetDetail.CurrentVersionNumber
      : !!version && !version.Subject
    if (!needsStoredHtml) {
      setEmailVersionHtml(null)
      return
    }
    // Both email types store the rendered HTML in RenderedHtmlBlobPath (BlobUrl is the design JSON).
    const blobUrl = version?.RenderedHtmlBlobUrl
    let cancelled = false
    setEmailVersionHtml(null)
    if (blobUrl) {
      fetch(blobUrl)
        .then(r => r.ok ? r.text() : null)
        .then(text => { if (!cancelled && text != null) setEmailVersionHtml(text) })
        .catch(err => {
          // A lapsed SAS URI surfaces as a fetch failure here; useSasUriRefresh re-fetches the
          // versions with fresh URIs, so this is recoverable — log at debug rather than swallow.
          console.debug('[AssetReview] version preview HTML fetch failed (SAS may have expired)', err)
        })
    }
    return () => { cancelled = true }
  }, [assetDetail?.Type, assetDetail?.CurrentVersionNumber, selectedVersionNumber, versions])

  // --- The email activations a copy's sign-off or reopen reaches (ORB-303) ---
  // Read again whenever its status or latest version changes, which is what signing off and reopening do.
  const reviewedCopyId = assetDetail?.Type === 'Email Copy' && assetDetail.Status !== 'Draft' ? assetDetail.Id : null
  useEffect(() => {
    setCopyActivations([])
    if (!reviewedCopyId) return
    let cancelled = false
    void assetApi.getEmailCopyActivations(clientCompanyId, workspaceId, campaignId, reviewedCopyId).then(response => {
      if (cancelled) return
      if (response.data) {
        setCopyActivations(response.data)
      } else if (response.error) {
        showError('Could not load the activations on this email', response.error)
      }
    })
    return () => { cancelled = true }
  }, [clientCompanyId, workspaceId, campaignId, reviewedCopyId, assetDetail?.Status, assetDetail?.CurrentVersionNumber, showError])

  // --- SAS URI refresh (silent — no loading state reset) ---

  const refreshSasUris = useCallback(async () => {
    if (!currentAsset) return
    const [detailRes, versionsRes] = await Promise.all([
      assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, currentAsset.Id),
      assetApi.getAssetVersions(clientCompanyId, workspaceId, currentAsset.Id),
    ])
    if (detailRes.data) {
      setAssetDetail(prev => prev ? { ...prev, ...detailRes.data! } : detailRes.data!)
    }
    if (versionsRes.data) {
      setVersions(versionsRes.data)
    }
  }, [clientCompanyId, workspaceId, campaignId, currentAsset?.Id])

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Lock body scroll + hide AI chat widget
  useBodyScrollLock()

  // --- Handlers ---

  const handlePrevious = currentIndex > 0
    ? () => {
        const newIndex = currentIndex - 1
        setCurrentIndex(newIndex)
        onAssetChange?.(assets[newIndex].Id)
      }
    : null

  const handleNext = currentIndex < assets.length - 1
    ? () => {
        const newIndex = currentIndex + 1
        setCurrentIndex(newIndex)
        onAssetChange?.(assets[newIndex].Id)
      }
    : null

  const handleVersionChange = (versionNumber: number) => {
    setSelectedVersionNumber(versionNumber)
  }

  const handleToggleExpand = useCallback(() => {
    setViewerExpanded(prev => !prev)
  }, [])

  const handleSignOff = async (comment?: string) => {
    if (!currentAsset || !assetDetail) return
    const dto: SignOffDto = {
      ETag: assetDetail.ETag,
      Confirmed: true,
      SignOffComment: comment,
    }
    const response = await assetApi.signOff(clientCompanyId, workspaceId, campaignId, currentAsset.Id, dto)
    if (response.error) {
      showErrorUnless422('Sign-off failed', response)
      return
    }
    if (response.data) {
      setAssetDetail(response.data)
      showSuccess('Signed off', `${response.data.Name} has been signed off.`)
      onAssetUpdated?.()
    }
  }

  const handleAddComment = async (text: string, parentCommentId?: string) => {
    if (!currentAsset || !assetDetail) return
    const response = await assetApi.addComment(clientCompanyId, workspaceId, campaignId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      Text: text,
      VersionNumber: selectedVersionNumber,
      ParentCommentId: parentCommentId,
    })
    if (response.error) {
      showErrorUnless422('Comment failed', response)
      return
    }
    if (response.data) {
      setComments(prev =>
        prev.some(c => c.CommentId === response.data!.CommentId) ? prev : [...prev, response.data!]
      )
      setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ETag } : prev)
    }
  }

  const handleUploadVersion = async (file: File, versionComment?: string) => {
    if (!currentAsset || !assetDetail) return
    const response = await assetApi.uploadVersion(clientCompanyId, workspaceId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      AssetId: currentAsset.Id,
      CampaignId: campaignId,
      WorkspaceId: workspaceId,
      FileName: file.name,
      MimeType: file.type || 'application/octet-stream',
      FileSizeBytes: file.size,
      VersionComment: versionComment,
    }, file)
    if (response.error) {
      showErrorUnless422('Upload failed', response)
      return
    }
    if (response.data) {
      setVersions(prev => [...prev, response.data!])
      setSelectedVersionNumber(response.data.VersionNumber)
      if (response.data.ParentAssetETag) {
        setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ParentAssetETag! } : prev)
      }
      showSuccess('Version uploaded', `v${response.data.VersionNumber} has been uploaded.`)
      onAssetUpdated?.()
    }
  }

  const handleDeleteAsset = async () => {
    if (!currentAsset) return
    const response = await assetApi.deleteAsset(clientCompanyId, workspaceId, campaignId, currentAsset.Id)
    if (response.error) {
      showErrorUnless422('Delete failed', response)
      throw response.error
    }
    showSuccess('Asset deleted', `${currentAsset.Name} has been deleted.`)
    onAssetUpdated?.()
    onClose()
  }

  const handleUploadVersionByCloudStorage = async (fileId: string, fileName: string, mimeType: string, provider: string, versionComment?: string) => {
    if (!currentAsset || !assetDetail || !userProfile?.Id) return

    // Pre-join SignalR group BEFORE the upload API call to avoid missing
    // fast completion notifications (race condition prevention)
    const cleanup = await preJoinForUpload('')

    const response = await assetApi.uploadVersionByCloudStorage(clientCompanyId, workspaceId, currentAsset.Id, {
      ETag: assetDetail.ETag,
      AssetId: currentAsset.Id,
      CampaignId: campaignId,
      WorkspaceId: workspaceId,
      CloudStorageFileId: fileId,
      CloudStorageFileName: fileName,
      CloudStorageMimeType: mimeType,
      CloudStorageProvider: provider,
      VersionComment: versionComment,
    })

    if (response.error) {
      cleanup() // Clean up SignalR subscription on failure
      showErrorUnless422('Upload failed', response)
      return
    }
    if (response.data) {
      setVersions(prev => [...prev, response.data!])
      setSelectedVersionNumber(response.data.VersionNumber)
      if (response.data.ParentAssetETag) {
        setAssetDetail(prev => prev ? { ...prev, ETag: response.data!.ParentAssetETag! } : prev)
      }

      // Now subscribe for this specific version's upload status
      subscribeToUploadStatus(response.data.Id)
    }
  }

  const applyAssetMutation = async (
    run: (assetId: string, etag: string) => Promise<ApiResponse<WebAssetViewDto>>,
    label: (updated: WebAssetViewDto) => string,
    failureTitle: string,
  ) => {
    if (!currentAsset || !assetDetail) return
    const assetId = currentAsset.Id
    const response = await run(assetId, assetDetail.ETag)
    if (response.status === 409) {
      showError('Conflict', 'This asset was modified elsewhere. Refreshing...')
      await loadAssetData(assetId)
      throw new Error('ETag conflict')
    }
    if (response.error) {
      showErrorUnless422(failureTitle, response)
      throw new Error(response.error)
    }
    if (response.data) {
      // Merge metadata but preserve existing SAS URIs to avoid unnecessary viewer reloads
      setAssetDetail(prev => {
        if (!prev) return response.data!
        return {
          ...response.data!,
          CurrentVersionBlobUrl: prev.CurrentVersionBlobUrl,
          CurrentVersionBlobUrlExpiresInSeconds: prev.CurrentVersionBlobUrlExpiresInSeconds,
        }
      })
      showSuccess('Asset updated', label(response.data))
      onAssetUpdated?.()
    }
  }

  const handleRenameAsset = (name: string) =>
    applyAssetMutation(
      (assetId, etag) => assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, assetId, {
        Id: assetId,
        ETag: etag,
        CampaignId: campaignId,
        Name: name,
      }),
      () => 'Name updated',
      'Update failed',
    )

  // Status and reopen are their own endpoints rather than a Status write on the asset PUT. That PUT
  // needs edit rights, which a reviewer does not have; and reopen is the only path that clears the
  // version's sign-off record and honours the web's InternalOnlyReopen setting (ORB-666). Reopen
  // lands on Ready for Sign-Off, which is the server's behaviour — the old raw write left the asset
  // in Reopened and left the version stamped.
  const handleStatusChange = (status: string) =>
    applyAssetMutation(
      (assetId, etag) => status === 'Reopened'
        ? assetApi.reopen(clientCompanyId, workspaceId, campaignId, assetId, { ETag: etag })
        : assetApi.changeStatus(clientCompanyId, workspaceId, campaignId, assetId, { ETag: etag, Status: status }),
      updated => `Status changed to ${updated.Status}`,
      status === 'Reopened' ? 'Reopen failed' : 'Status change failed',
    )

  const permissions = useAssetReviewPermissions(clientCompanyId, workspaceId)

  // Send Test (ORB-441) — the same Email Copy self-test as the builder, surfaced on the
  // review screen so a reviewer can mail themselves exactly what the preview shows before
  // signing off. Gated on workflow participation rather than the edit role: a client contact
  // approving an email has to be able to see it in a real inbox, and the send only ever goes to
  // the caller's own address (ORB-666).
  const { sendTest, sendingTest } = useSendTestEmail()

  // Send exactly what the preview renders: the selected version by number (ORB-303), the stored HTML of one saved
  // before versions kept their subject (emailVersionHtml), or the live draft of a copy with no version yet. Stored HTML
  // loads async (blob fetch) and is null while in flight — hold the action until it's in hand so the send matches the
  // selected version rather than the draft. The asset says whether a version exists: a version list that failed to
  // load is not a copy without one.
  const copyHasNoVersion = assetDetail?.Type === 'Email Copy' && !assetDetail.CurrentVersionNumber
  const sendTestReady = copyVersionNumber !== undefined || copyHasNoVersion || emailVersionHtml != null
  const canSendTest = assetDetail?.Type === 'Email Copy' && assetDetail?.Status !== 'Draft' && permissions.isWorkflowParticipant
  const handleSendTest = useCallback(async () => {
    if (!assetDetail || !sendTestReady) return
    await sendTest({
      clientCompanyId,
      workspaceId,
      campaignId,
      assetId: assetDetail.Id,
      body: copyVersionNumber !== undefined ? { VersionNumber: copyVersionNumber } : { HtmlOverride: emailVersionHtml ?? undefined },
    })
  }, [sendTest, clientCompanyId, workspaceId, campaignId, assetDetail, emailVersionHtml, copyVersionNumber, sendTestReady])

  const canEditInWebster = permissions.canEditInWebster && assetDetail && isWebsterAssetType(assetDetail.Type) && !!onEditInWebster

  const handleEditInWebster = useCallback(() => {
    if (assetDetail && onEditInWebster) {
      onEditInWebster(assetDetail.Id, assetDetail.Type)
    }
  }, [assetDetail, onEditInWebster])

  // Determine which BlobUrl/fileName to show based on selected version
  const viewerBlobUrl = selectedVersion?.BlobUrl ?? assetDetail?.CurrentVersionBlobUrl ?? null
  const viewerFileName = selectedVersion?.OriginalFileName ?? assetDetail?.CurrentVersionFileName ?? null
  const viewerMimeType = selectedVersion?.MimeType ?? null

  // --- SAS URI auto-refresh before expiry ---
  const selectedVersionExpiry = selectedVersion?.BlobUrlExpiresInSeconds
  const detailExpiry = assetDetail?.CurrentVersionBlobUrlExpiresInSeconds
  const shortestExpiry = [selectedVersionExpiry, detailExpiry]
    .filter((v): v is number => v != null && v > 0)
    .reduce((min, v) => Math.min(min, v), Infinity)

  useSasUriRefresh(
    shortestExpiry === Infinity ? null : shortestExpiry,
    refreshSasUris
  )

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900">
      <AssetReviewTopBar
        onClose={onClose}
        onPrevious={handlePrevious}
        onNext={handleNext}
        currentIndex={currentIndex}
        totalAssets={assets.length}
        onEditInWebster={canEditInWebster ? handleEditInWebster : undefined}
        onSendTest={canSendTest ? handleSendTest : undefined}
        sendingTest={sendingTest}
        sendTestDisabled={!sendTestReady}
      />

      {error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
            <button
              onClick={() => currentAsset && loadAssetData(currentAsset.Id)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : loading && !assetDetail ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      ) : assetDetail?.Status === 'Draft' ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              This asset is still being drafted and isn't available for review yet.
            </p>
            {canEditInWebster && (
              <button
                onClick={handleEditInWebster}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${BRAND_CLASSES.BUTTON.PRIMARY}`}
              >
                Open in builder
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left panel: LP preview, Email editors, or Document Viewer */}
          <div className="flex-1 min-w-0">
            {assetDetail?.Type === 'Landing Page' ? (
              <HtmlPreview
                html={lpHtml}
                thankYouHtml={lpThankYouHtml}
                hasEventStates={hasEventStates}
                quiz={web?.Download?.Quiz ?? null}
              />
            ) : assetDetail?.Type === 'Email Template' ? (
              // Server-rendered iframe instead of the editable Tiptap canvas — mirrors the
              // Email Copy preview pane so what reviewers see here matches what the recipient
              // gets in their inbox (table-based centering, sandboxed HTML, no editor quirks).
              <EmailTemplatePreview
                clientCompanyId={clientCompanyId}
                workspaceId={workspaceId}
                campaignId={campaignId}
                templateAssetId={assetDetail.Id}
                htmlOverride={emailVersionHtml}
              />
            ) : assetDetail?.Type === 'Email Copy' ? (
              // Read-only server-rendered preview — matches the live editor's preview
              // pane so reviewers see exactly what the recipient gets: the selected version, which is
              // what a sign-off approves (ORB-303), and the live draft only before any version exists.
              <EmailCopyPreview
                clientCompanyId={clientCompanyId}
                workspaceId={workspaceId}
                campaignId={campaignId}
                assetId={assetDetail.Id}
                subject={selectedVersion?.Subject ?? assetDetail.Subject}
                subjectVariants={selectedVersion?.Subject ? selectedVersion.SubjectVariants : assetDetail.SubjectVariants}
                htmlOverride={emailVersionHtml}
                versionNumber={copyVersionNumber}
              />
            ) : (
              <AssetDocumentViewer
                blobUrl={viewerBlobUrl}
                fileName={viewerFileName}
                mimeType={viewerMimeType}
                expanded={viewerExpanded}
                onToggleExpand={handleToggleExpand}
              />
            )}
          </div>

          {/* Side Panel */}
          {!viewerExpanded && <div className="w-[400px] xl:w-[440px] flex-shrink-0 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <AssetReviewSidePanel
              assetDetail={assetDetail}
              versions={versions}
              comments={comments}
              selectedVersionNumber={selectedVersionNumber}
              onVersionChange={handleVersionChange}
              onSignOff={handleSignOff}
              onAddComment={handleAddComment}
              onUploadVersion={handleUploadVersion}
              onUploadVersionByCloudStorage={handleUploadVersionByCloudStorage}
              onStatusChange={handleStatusChange}
              onNameChange={handleRenameAsset}
              onDeleteAsset={handleDeleteAsset}
              loading={loading}
              permissions={permissions}
              qaRun={qaRunForSelectedVersion}
              qaLoading={qaLoadingForSelectedVersion}
              qaLoadError={qaLoadErrorForSelectedVersion}
              qaApplies={qaApplies}
              onOverrideQaFinding={handleOverrideQaFinding}
              onGoToWebTab={onGoToWebTab}
              copyActivations={copyActivations}
            />
          </div>}
        </div>
      )}
    </div>
  )
}
